import { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthedState } from '../hooks/useSessionInterceptor';

export interface User {
  id: string;
  email: string;
  name: string;
  provider: string;
  avatarUrl?: string;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    fetch('/api/auth/status')
      .then((res) => {
        if (res.ok) return res.json() as Promise<Record<string, unknown>>;
        setAuthedState(false);
        return null;
      })
      .then((data) => {
        if (!cancelled && data?.authenticated) {
          return fetch('/api/auth/me').then((meRes) => {
            if (meRes.ok) return meRes.json() as Promise<User>;
            return null;
          });
        }
        return null;
      })
      .then((user) => {
        if (!cancelled && user) {
          setUser(user);
          queryClient.setQueryData(['auth-user'], user);
          setAuthedState(true);
        } else if (!cancelled) {
          setAuthedState(false);
        }
      })
      .catch((err) => {
        // Network error, CORS, server down — gracefully degrade.
        // User remains unauthenticated; login flow still works.
        console.warn('Auth: failed to fetch session (backend may be down):', err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const login = (provider: string) => {
    window.location.href = `/oauth2/authorization/${provider}`;
  };

  const logout = () => {
    // credentials: 'include' ensures the JSESSIONID cookie is sent to the server
    // so the session can be invalidated server-side.
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
      .then(() => {
        setAuthedState(false);
        // Clear the entire React Query cache so no stale auth state remains.
        queryClient.clear();
        setUser(null);
      })
      .catch(() => {
        // Best-effort server cleanup — always clear local state.
        setAuthedState(false);
        queryClient.clear();
        setUser(null);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
